<!-- app-subdomain-creators\src\accountPages\CreatorMemberDetails.vue -->

<template>
  <div class="ml-16 max-w-7xl lg:flex lg:gap-x-10 lg:px-8">
    <h1 class="sr-only">
      Member Details
    </h1>

    <aside class="flex overflow-x-auto border-b border-gray-900/5 py-2 lg:block lg:w-64 lg:flex-none lg:border-0">
      <nav class="flex-none px-4 sm:px-6 lg:px-0">
        <ul
          role="list"
          class="flex gap-x-3 gap-y-0.5 whitespace-nowrap lg:flex-col"
        >
          <li
            v-for="item in memberDetailNavigation"
            :key="item.name"
          >
            <a
              href="#"
              :class="[item.current ? 'bg-gray-50 text-[#2D8CFF]' : 'text-gray-700 hover:bg-gray-50 hover:text-[#2D8CFF]', 'group flex gap-x-2.5 rounded-md py-1.5 pl-2 pr-2.5 text-sm font-semibold leading-6']"
              @click.prevent="setCurrentSection(item.name)"
            >
              <component
                :is="item.icon"
                :class="[item.current ? 'text-[#2D8CFF]' : 'text-gray-400 group-hover:text-[#2D8CFF]', 'h-5 w-5 shrink-0']"
                aria-hidden="true"
              />
              {{ item.name }}
            </a>
          </li>
        </ul>
      </nav>
    </aside>

    <main class="px-4 py-2 sm:px-6 lg:flex-auto lg:px-0">
      <div class="mx-auto max-w-2xl space-y-4 sm:space-y-5 lg:mx-0 lg:max-w-none">
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-1"
        >
          <div
            v-if="!isProfileSection"
            class="mb-1 px-1 py-1"
          >
            <div class="flex items-center gap-3">
              <div class="relative h-10 w-10">
                <div class="h-10 w-10 overflow-hidden rounded-full bg-zinc-100">
                  <img
                    v-if="memberSummaryAvatarReady"
                    :src="memberSummaryAvatarSrc"
                    alt="Member avatar"
                    class="h-10 w-10 object-cover"
                    @error="onMemberSummaryAvatarError"
                  >
                  <div
                    v-else
                    class="h-10 w-10 animate-pulse rounded-full bg-zinc-200"
                    aria-hidden="true"
                  />
                </div>
                <span
                  class="absolute -bottom-0.5 -right-0.5 z-20 h-3 w-3 rounded-full ring-2 ring-white"
                  :class="memberSummaryStatusClass"
                />
              </div>
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-zinc-900">
                  {{ memberSummary?.name || (memberSummaryLoading ? 'Loading member...' : 'Member') }}
                </p>
                <p class="truncate text-xs text-zinc-500">
                  {{ memberSummary?.email || '' }}
                </p>
              </div>
            </div>
          </div>
        </Transition>

        <Transition
          mode="out-in"
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-1"
        >
          <div :key="currentSectionName">
            <component
              :is="currentComponent"
              :member-id="normalizedMemberId"
            />
          </div>
        </Transition>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, markRaw, ref, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useWebsocketStore } from '@/stores/websocketStore';
import { usePresenceStore } from '@/stores/presenceStore';

import {
  CursorArrowRippleIcon,
  CreditCardIcon,
  ArrowPathIcon,
  CubeIcon,
  FingerPrintIcon,
  UserCircleIcon,
  AcademicCapIcon,
  DocumentTextIcon,
} from '@heroicons/vue/24/outline';

// Components for each section (ensure paths match the actual filenames)
import MemberDetailsProfile from '../Components/MemberDetailsProfile.vue';
import MemberDetailsProducts from '../Components/MemberDetailsProducts.vue';
import MemberDetailsSubscriptions from '../Components/MemberDetailsSubscriptions.vue';
import MemberDetailsPaymentMethods from '../Components/MemberDetailsPaymentMethods.vue';
import MemberDetailsNotes from '../Components/MemberDetailsNotes.vue';
import MemberDetailsActivities from '../Components/MemberDetailsActivities.vue';
import MemberDetailsLoginSessions from '../Components/MemberDetailsLoginSessions.vue';
import MemberDetailsCourseProgress from '../Components/MemberDetailsCourseProgress.vue';

// Use the route object to get the memberId from the params
const route = useRoute();
const memberId = ref(route.params.memberId);
const websocketStore = useWebsocketStore();
const presenceStore = usePresenceStore();
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const apiBackendUrl = process.env.VUE_APP_PLATFORMA_API_BACKEND_URL;
const defaultProfilePic = require('@/assets/media/images/default-avatar.png');

const memberSummary = ref(null);
const memberSummaryLoading = ref(false);
const memberSummaryAvatarErrored = ref(false);
const subscribedPresenceMemberId = ref(0);
const memberPresenceStatus = computed(() => {
  const id = Number(memberSummary.value?.userId || 0);
  if (!Number.isInteger(id) || id <= 0) return 'offline';
  return presenceStore.memberStatus(id);
});
const memberSummaryStatusClass = computed(() => {
  switch (memberPresenceStatus.value) {
    case 'online':
      return 'bg-green-400';
    case 'away':
      return 'bg-yellow-400';
    case 'offline':
    default:
      return 'bg-gray-400';
  }
});
const memberSummaryAvatarReady = computed(() => {
  return Boolean(memberSummary.value) || !memberSummaryLoading.value;
});
const memberSummaryAvatarSrc = computed(() => {
  const customAvatar = String(memberSummary.value?.profilePic || '').trim();
  if (customAvatar && !memberSummaryAvatarErrored.value) return customAvatar;
  return defaultProfilePic;
});
const onMemberSummaryAvatarError = () => {
  const customAvatar = String(memberSummary.value?.profilePic || '').trim();
  if (!customAvatar) return;
  memberSummaryAvatarErrored.value = true;
};

const sectionComponents = Object.freeze({
  Profile: markRaw(MemberDetailsProfile),
  Products: markRaw(MemberDetailsProducts),
  Subscriptions: markRaw(MemberDetailsSubscriptions),
  'Payment Methods': markRaw(MemberDetailsPaymentMethods),
  Notes: markRaw(MemberDetailsNotes),
  Activities: markRaw(MemberDetailsActivities),
  'Course Progress': markRaw(MemberDetailsCourseProgress),
  'Login Sessions': markRaw(MemberDetailsLoginSessions),
});

const memberDetailNavigation = ref([
  { name: 'Profile', icon: markRaw(UserCircleIcon), componentKey: 'Profile', current: true },
  { name: 'Products', icon: markRaw(CubeIcon), componentKey: 'Products', current: false },
  { name: 'Subscriptions', icon: markRaw(ArrowPathIcon), componentKey: 'Subscriptions', current: false },
  { name: 'Payment Methods', icon: markRaw(CreditCardIcon), componentKey: 'Payment Methods', current: false },
  { name: 'Activities', icon: markRaw(CursorArrowRippleIcon), componentKey: 'Activities', current: false },
  { name: 'Course Progress', icon: markRaw(AcademicCapIcon), componentKey: 'Course Progress', current: false },
  { name: 'Login Sessions', icon: markRaw(FingerPrintIcon), componentKey: 'Login Sessions', current: false },
  { name: 'Notes', icon: markRaw(DocumentTextIcon), componentKey: 'Notes', current: false },
]);

// State to manage the current section/component
const currentComponent = shallowRef(sectionComponents.Profile);
const currentSectionName = computed(() => (
  memberDetailNavigation.value.find((item) => item.current)?.name || 'Profile'
));
const isProfileSection = computed(() => currentSectionName.value === 'Profile');
const normalizedMemberId = computed(() => String(memberId.value ?? route.params.memberId ?? '').trim());

// Set the current section and update the active component
const setCurrentSection = (sectionName) => {
  memberDetailNavigation.value.forEach((item) => {
    item.current = item.name === sectionName;
    if (item.current) {
      currentComponent.value = sectionComponents[item.componentKey] || sectionComponents.Profile;
    }
  });
};

const resolveRouteSection = (sectionValue) => {
  const token = String(sectionValue || '').trim().toLowerCase();
  if (!token) return '';
  if (token === 'subscriptions' || token === 'subscription' || token === 'billing') return 'Subscriptions';
  if (token === 'profile') return 'Profile';
  if (token === 'products') return 'Products';
  if (token === 'payment_methods' || token === 'payment-methods' || token === 'payment methods') return 'Payment Methods';
  if (token === 'activities') return 'Activities';
  if (token === 'course_progress' || token === 'course-progress' || token === 'course progress') return 'Course Progress';
  if (token === 'login_sessions' || token === 'login-sessions' || token === 'login sessions') return 'Login Sessions';
  if (token === 'notes') return 'Notes';
  return '';
};

const subscribeToMemberPresence = (memberUserId) => {
  const id = Number(memberUserId || 0);
  if (!Number.isInteger(id) || id <= 0) return;
  if (subscribedPresenceMemberId.value === id) return;
  subscribedPresenceMemberId.value = id;
  websocketStore.send({
    type: 'creator',
    event: 'presence:subscribeMembers',
    payload: {
      memberIds: [id],
    },
  });
};

const requestBases = computed(() => {
  const rows = Array.from(new Set(
    [apiProxyUrl, apiBackendUrl].map((value) => String(value || '').trim()).filter(Boolean)
  ));
  return rows.length ? rows : [''];
});

const loadMemberSummary = async () => {
  const id = Number(memberId.value || 0);
  if (!Number.isInteger(id) || id <= 0) {
    memberSummary.value = null;
    memberSummaryAvatarErrored.value = false;
    return;
  }

  memberSummaryLoading.value = true;
  memberSummaryAvatarErrored.value = false;
  try {
    let response = null;
    let payload = {};
    let lastError = '';
    for (const base of requestBases.value) {
      response = await fetch(`${base}/api/members/${encodeURIComponent(String(id))}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.success !== false) break;
      lastError = String(payload?.message || payload?.error || `Failed loading member (${response.status})`);
      if (response.status !== 404) break;
    }

    if (!response || !response.ok || payload?.success === false || !payload?.member) {
      throw new Error(lastError || `Failed loading member (${response?.status || 500})`);
    }

    const profilePicRaw = String(payload.member.profile_pic || '').trim();
    const profilePic = profilePicRaw && profilePicRaw !== 'src/assets/media/images/default-avatar.png'
      ? profilePicRaw
      : '';
    const memberUserId = Number(payload.member.user_id || 0);
    memberSummary.value = {
      userId: Number.isInteger(memberUserId) && memberUserId > 0 ? memberUserId : null,
      name: `${String(payload.member.first_name || '').trim()} ${String(payload.member.last_name || '').trim()}`.trim() || 'Member',
      email: String(payload.member.email || '').trim(),
      profilePic,
    };
    subscribeToMemberPresence(memberUserId);
  } catch (_error) {
    memberSummary.value = null;
    memberSummaryAvatarErrored.value = false;
  } finally {
    memberSummaryLoading.value = false;
  }
};

watch(
  () => route.params.memberId,
  (nextMemberId) => {
    memberId.value = nextMemberId;
    void loadMemberSummary();
  },
  { immediate: true }
);

watch(
  () => currentSectionName.value,
  (section) => {
    if (section !== 'Profile') {
      void loadMemberSummary();
    }
  }
);

watch(
  () => route.query.section,
  (nextSection) => {
    const sectionName = resolveRouteSection(nextSection);
    if (!sectionName) return;
    setCurrentSection(sectionName);
  },
  { immediate: true }
);

</script>

